import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

/* Layout */
import Layout from '@/layout/index.vue'

/* Router modules */
import componentsRouter from './modules/components'
import chartsRouter from './modules/charts'
import tableRouter from './modules/table'
import nestedRouter from './modules/nested'
import subscriptionRouter from './modules/subscription'

Vue.use(VueRouter)

/*
  Note: sub-menu only appear when children.length>=1
  Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
*/

/*
  name:'router-name'             the name field is required when using <keep-alive>, it should also match its component's name property
                                 detail see : https://vuejs.org/v2/guide/components-dynamic-async.html#keep-alive-with-Dynamic-Components
  redirect:                      if set to 'noredirect', no redirect action will be trigger when clicking the breadcrumb
  meta: {
    roles: ['admin', 'editor']   will control the page roles (allow setting multiple roles)
    title: 'title'               the name showed in subMenu and breadcrumb (recommend set)
    icon: 'svg-name'             the icon showed in the sidebar
    hidden: true                 if true, this route will not show in the sidebar (default is false)
    alwaysShow: true             if true, will always show the root menu (default is false)
                                 if false, hide the root menu when has less or equal than one children route
    breadcrumb: false            if false, the item will be hidden in breadcrumb (default is true)
    noCache: true                if true, the page will not be cached (default is false)
    affix: true                  if true, the tag will affix in the tags-view
    activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
  }
*/

/**
  ConstantRoutes
  a base page that does not have permission requirements
  all roles can be accessed
*/
export const constantRoutes: RouteConfig[] = [
  {
    path: '/redirect',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () =>
          import(
            /* webpackChunkName: "redirect" */ '@/views/redirect/index.vue'
          )
      }
    ]
  },
  {
    path: '/login',
    component: () =>
      import(/* webpackChunkName: "login" */ '@/views/login/email-login.vue'),
    meta: { hidden: true }
  },
  {
    path: '/email-login',
    name: 'email-login',
    component: () =>
      import(/* webpackChunkName: "login" */ '@/views/login/email-login.vue'),
    meta: { hidden: true }
  },
  {
    path: '/auth-redirect',
    component: () =>
      import(
        /* webpackChunkName: "auth-redirect" */ '@/views/login/auth-redirect.vue'
      ),
    meta: { hidden: true }
  },
  {
    path: '/404',
    component: () =>
      import(/* webpackChunkName: "404" */ '@/views/error-page/404.vue'),
    meta: { hidden: true }
  },
  {
    path: '/401',
    component: () =>
      import(/* webpackChunkName: "401" */ '@/views/error-page/401.vue'),
    meta: { hidden: true }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    meta: {
      title: 'dashboard',
      icon: 'dashboard'
    },
    children: [
      {
        path: 'dashboard',
        component: () =>
          import(
            /* webpackChunkName: "business-analytics" */ '@/views/business-analytics/index.vue'
          ),
        name: 'Dashboard',
        meta: {
          title: 'dashboard',
          icon: 'dashboard',
          affix: true
        }
      },
      {
        path: 'bulk-grant-cleanup',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/bulkGrantCleanup/index.vue'),
        name: 'BulkGrantCleanup',
        meta: {
          title: 'bulkGrantCleanup',
          icon: 'eye-open',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'kpi-analytics',
        component: () => import('@/views/kpi-analytics/index.vue'),
        name: 'KpiAnalyticsDashboard',
        meta: {
          title: 'kpiAnalytics',
          icon: 'chart',
          roles: ['admin']
        }
      },
      {
        path: 'subscription-audit',
        component: () => import('@/views/subscriptionAudit/index.vue'),
        name: 'SubscriptionAudit',
        meta: {
          title: 'subscriptionAudit',
          icon: 'bug',
          roles: ['super-admin']
        }
      },
      {
        path: 'aim-analytics',
        component: () => import('@/views/aimAnalytics/index.vue'),
        name: 'AimAnalytics',
        meta: {
          title: 'aimAnalytics',
          icon: 'chart',
          roles: ['super-admin']
        }
      },
      {
        path: 'diagnostics',
        component: () => import('@/views/diagnostics/index.vue'),
        name: 'Diagnostics',
        meta: {
          title: 'diagnostics',
          icon: 'eye-on',
          roles: ['admin']
        }
      },
      {
        path: 'admin-users',
        component: () => import('@/views/adminUsers/index.vue'),
        name: 'AdminUsers',
        meta: {
          title: 'adminUsers',
          icon: 'peoples',
          roles: ['admin']
        }
      },
      {
        path: 'apple-notifications',
        component: () => import('@/views/appleNotifications/index.vue'),
        name: 'AppleNotifications',
        meta: {
          title: 'appleNotifications',
          icon: 'shopping',
          roles: ['admin']
        }
      },
      {
        path: 'saved-routes',
        component: () => import('@/views/routes/index.vue'),
        name: 'SavedRoutes',
        meta: {
          title: 'savedRoutes',
          icon: 'guide',
          roles: ['admin']
        }
      }
    ]
  },
  // {
  //   path: '/documentation',
  //   component: Layout,
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "documentation" */ '@/views/documentation/index.vue'),
  //       name: 'Documentation',
  //       meta: { title: 'documentation', icon: 'documentation', affix: true }
  //     }
  //   ]
  // },
  // {
  //   path: '/guide',
  //   component: Layout,
  //   redirect: '/guide/index',
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "guide" */ '@/views/guide/index.vue'),
  //       name: 'Guide',
  //       meta: {
  //         title: 'guide',
  //         icon: 'guide',
  //         noCache: true
  //       }
  //     }
  //   ]
  // },
  {
    path: '/profile',
    component: Layout,
    redirect: '/profile/index',
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        component: () =>
          import(/* webpackChunkName: "profile" */ '@/views/profile/index.vue'),
        name: 'Profile',
        meta: {
          title: 'profile',
          icon: 'user',
          noCache: true
        }
      }
    ]
  }
]

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */
export const asyncRoutes: RouteConfig[] = [
  {
    path: '/users',
    component: Layout,
    redirect: '/users/list',
    meta: {
      title: 'users',
      icon: 'user',
      // alwaysShow: true, // will always show the root menu
      roles: ['admin', 'product-team']
    },
    children: [
      {
        path: 'list',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/users/index.vue'),
        name: 'ListUser',
        meta: {
          title: 'listUser',
          icon: 'user'
        }
      },
      {
        path: 'activations',
        component: () => import('@/views/users/activations.vue'),
        name: 'activations',
        meta: { title: 'activations', icon: 'size' }
      },
      {
        path: 'auth-events',
        component: () => import('@/views/users/authevents.vue'),
        name: 'AuthEvents',
        meta: { title: 'authEvents', icon: 'lock' }
      }
    ]
  },
  {
    path: '/app-settings',
    component: Layout,
    redirect: '/app-settings/configs',
    meta: {
      title: 'appSettings',
      icon: 'guide',
      roles: ['admin']
    },
    children: [
      {
        path: 'places-provider',
        component: () => import('@/views/placesProvider/index.vue'),
        name: 'PlacesProvider',
        meta: {
          title: 'placesProvider',
          icon: 'guide',
          roles: ['admin']
        }
      },
      {
        path: 'mcp',
        component: () => import('@/views/mcp/index.vue'),
        name: 'Mcp',
        meta: {
          title: 'mcp',
          icon: 'guide',
          roles: ['admin']
        }
      },
      {
        path: 'places-compare',
        component: () => import('@/views/placesCompare/index.vue'),
        name: 'PlacesCompare',
        meta: {
          title: 'placesCompare',
          icon: 'guide',
          roles: ['admin']
        }
      },
      {
        path: 'configs',
        component: () =>
          import(
            /* webpackChunkName: "configs" */ '@/views/configs/index.vue'
          ),
        name: 'configs',
        meta: {
          title: 'configs',
          icon: 'list'
        }
      },
      {
        path: 'storeVersion',
        component: () =>
          import(
            /* webpackChunkName: "storeVersion" */ '@/views/storeVersion/index.vue'
          ),
        name: 'storeVersion',
        meta: {
          title: 'storeVersion',
          icon: 'component'
        }
      },
      {
        path: 'assistantConfig',
        component: () =>
          import(
            /* webpackChunkName: "assistantConfig" */ '@/views/assistantConfig/index.vue'
          ),
        name: 'assistantConfig',
        meta: {
          title: 'assistantConfig',
          icon: 'guide'
        }
      },
      {
        path: 'navigationConfig',
        component: () =>
          import(
            /* webpackChunkName: "navigationConfig" */ '@/views/navigationConfig/index.vue'
          ),
        name: 'navigationConfig',
        meta: {
          title: 'navigationConfig',
          icon: 'guide'
        }
      },
      {
        path: 'contracts',
        component: () =>
          import(
            /* webpackChunkName: "contracts" */ '@/views/contracts/index.vue'
          ),
        name: 'ContractManagement',
        meta: {
          title: 'contractManagement',
          icon: 'documentation'
        }
      },
      {
        path: 'external-api-keys',
        component: () =>
          import(
            /* webpackChunkName: "external-api-keys" */ '@/views/external-api-keys/index.vue'
          ),
        name: 'ExternalApiKeys',
        meta: {
          title: 'externalApiKeys',
          icon: 'lock'
        }
      },
      {
        path: 'bulk-grant',
        component: () =>
          import(
            /* webpackChunkName: "bulk-grant" */ '@/views/bulk-grant/index.vue'
          ),
        name: 'BulkGrant',
        meta: {
          title: 'bulkGrant',
          icon: 'peoples'
        }
      },
      {
        path: 'reporting',
        component: () =>
          import(
            /* webpackChunkName: "reporting" */ '@/views/reporting/index.vue'
          ),
        name: 'Reporting',
        meta: {
          title: 'reporting',
          icon: 'chart'
        }
      }
    ]
  },
  {
    path: '/mobile',
    component: Layout,
    redirect: '/mobile/localizations',
    meta: {
      title: 'mobile',
      icon: 'qq',
      roles: ['admin', 'product-team']
    },
    children: [
      {
        path: 'localizations',
        component: () => import('@/views/applocalizations/index.vue'),
        name: 'applocalizations',
        meta: { title: 'applocalizations', icon: 'size' }
      },
      {
        path: 'otafiles',
        component: () => import('@/views/applocalizations/otafiles.vue'),
        name: 'otafiles',
        meta: { title: 'otafiles', icon: 'size' }
      },
      {
        path: 'beta-groups',
        component: () => import('@/views/beta-groups/index.vue'),
        name: 'betaGroups',
        meta: { title: 'betaGroups', icon: 'peoples' }
      },
      {
        path: 'cane-audio',
        component: () => import(/* webpackChunkName: "cane-audio" */ '@/views/cane-audio/index.vue'),
        name: 'caneAudioPacks',
        meta: { title: 'caneAudioPacks', icon: 'size', roles: ['admin'] }
      },
      {
        path: 'localizations/detail',
        component: () => import('@/views/applocalizations/detail.vue'),
        name: 'applocalizations_detail',
        meta: { hidden: true, title: 'applocalizations_detail', icon: 'size' }
      },
      {
        path: 'coupons',
        component: () => import('@/views/appcoupons/index.vue'),
        name: 'appcoupons',
        meta: { title: 'appcoupons', icon: 'shopping' }
      }
    ]
  },
  {
    path: '/mobility',
    component: Layout,
    redirect: '/mobility/addTrainerTrainee',
    meta: {
      title: 'mobility',
      icon: 'tree',
      roles: ['admin'], // you can set roles in root nav
      alwaysShow: true // will always show the root menu
    },
    children: [
      {
        path: 'addTrainerTrainee',
        component: () => import('@/views/mobility/addTrainerTrainee.vue'),
        name: 'AddTrainerTrainee',
        meta: {
          title: 'addTrainerTrainee',
          roles: ['admin'],
          icon: 'education'
        }
      },
      {
        path: 'aim',
        component: () => import('@/views/aim/index.vue'),
        name: 'AimData',
        meta: {
          title: 'aimData',
          roles: ['admin'],
          icon: 'chart'
        }
      }
      // {
      //   path: 'listOrEditTrainerTrainee',
      //   component: () => import('@/views/mobility/listOrEditTrainerTrainee.vue'),
      //   name: 'ListOrEditTrainerTrainee',
      //   meta: {
      //     title: 'listOrEditTrainerTrainee',
      //     roles: ['admin']
      //   }
      // }
    ]
  },
  {
    path: '/notifications',
    component: Layout,
    redirect: '/notifications/logs',
    meta: {
      title: 'notifications',
      icon: 'message',
      roles: ['admin'],
      alwaysShow: false
    },
    children: [
      {
        path: 'logs',
        component: () => import('@/views/notifications/index.vue'),
        name: 'NotificationLogs',
        meta: {
          title: 'notificationLogs',
          roles: ['admin'],
          icon: 'message'
        }
      }
    ]
  },
  {
    path: '/entitlements',
    component: Layout,
    redirect: '/entitlements/features',
    meta: {
      title: 'entitlements',
      icon: 'lock',
      roles: ['admin', 'product-team'],
      alwaysShow: true
    },
    children: [
      {
        path: 'features',
        component: () => import('@/views/features/index.vue'),
        name: 'Features',
        meta: {
          title: 'features',
          icon: 'star',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'devices',
        component: () => import('@/views/devices/index.vue'),
        name: 'Devices',
        meta: {
          title: 'devices',
          icon: 'link',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'warranty-periods',
        component: () => import('@/views/warranty-periods/index.vue'),
        name: 'WarrantyPeriods',
        meta: {
          title: 'warrantyPeriods',
          icon: 'chart',
          roles: ['admin']
        }
      },
      {
        path: 'products',
        component: () => import('@/views/entitlement-products/index.vue'),
        name: 'EntitlementProducts',
        meta: {
          title: 'entitlementProducts',
          icon: 'component',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'rules',
        component: () => import('@/views/rules/index.vue'),
        name: 'EntitlementRules',
        meta: {
          title: 'entitlementRules',
          icon: 'tree-table',
          roles: ['admin']
        }
      },
      {
        path: 'business-analytics',
        component: () => import('@/views/business-analytics/index.vue'),
        name: 'BusinessAnalytics',
        meta: {
          title: 'businessAnalytics',
          icon: 'chart',
          hidden: true,
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'referrals',
        component: () => import('@/views/referrals/index.vue'),
        name: 'Referrals',
        meta: {
          title: 'referrals',
          icon: 'peoples',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'pending-grants',
        component: () => import('@/views/pendingGrants/index.vue'),
        name: 'PendingGrants',
        meta: {
          title: 'pendingGrants',
          icon: 'clipboard',
          roles: ['admin']
        }
      },
      {
        path: 'skus',
        component: () => import('@/views/skus/index.vue'),
        name: 'Skus',
        meta: {
          title: 'skuManagement',
          icon: 'nested',
          roles: ['admin']
        }
      },
      {
        path: 'campaigns',
        component: () => import('@/views/campaigns/index.vue'),
        name: 'Campaigns',
        meta: {
          title: 'campaigns',
          icon: 'shopping',
          roles: ['admin']
        }
      },
      {
        path: 'monthly-credits',
        component: () => import('@/views/monthly-credits-config/index.vue'),
        name: 'MonthlyCreditsConfig',
        meta: {
          title: 'monthlyCredits',
          icon: 'money',
          roles: ['admin']
        }
      }
    ]
  },
  {
    path: '/zoho',
    component: Layout,
    children: [
      {
        path: 'index',
        component: () => import('@/views/zoho/index.vue'),
        name: 'Zoho',
        meta: {
          title: 'zoho',
          icon: 'international',
          roles: ['admin']
        }
      }
    ]
  },
  {
    path: '/danis',
    component: Layout,
    redirect: '/danis/config',
    name: 'danis',
    meta: {
      title: 'danis',
      icon: 'wechat',
      // alwaysShow: true, // will always show the root menu
      roles: ['admin', 'product-team']
    },
    children: [
      {
        path: 'freeZone',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/freeZone/index.vue'),
        name: 'freeZone',
        meta: {
          title: 'freeZone',
          icon: 'people',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'company',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/company/index.vue'),
        name: 'company',
        meta: {
          title: 'company',
          icon: 'component',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'subject',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/subject/index.vue'),
        name: 'subject',
        meta: {
          title: 'subject',
          icon: 'tree-table',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'brand',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/brand/index.vue'),
        name: 'brand',
        meta: {
          title: 'brand',
          icon: 'tree-table',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'brand-subject',
        component: () =>
          import(
            /* webpackChunkName: "zip" */ '@/views/brandSubject/index.vue'
          ),
        name: 'brandSubject',
        meta: {
          title: 'brandSubject',
          icon: 'tree-table',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'redirect-weassist',
        component: () =>
          import(
            /* webpackChunkName: "zip" */ '@/views/login/redirect-weassist.vue'
          ),
        name: 'redirect-weassist',
        meta: {
          title: 'redirect-weassist',
          icon: 'password',
          roles: ['admin', 'product-team']
        }
      },
      {
        path: 'call-logs',
        component: () =>
          import(/* webpackChunkName: "zip" */ '@/views/callEvents/index.vue'),
        name: 'CallLogs',
        meta: {
          title: 'callLogs',
          icon: 'message',
          roles: ['admin', 'product-team']
        }
      }
    ]
  },
  // {
  //   path: '/permission',
  //   component: Layout,
  //   redirect: '/permission/directive',
  //   meta: {
  //     title: 'permission',
  //     icon: 'lock',
  //     roles: ['admin'], // you can set roles in root nav
  //     alwaysShow: true // will always show the root menu
  //   },
  //   children: [
  //     {
  //       path: 'page',
  //       component: () => import(/* webpackChunkName: "permission-page" */ '@/views/permission/page.vue'),
  //       name: 'PagePermission',
  //       meta: {
  //         title: 'pagePermission',
  //         roles: ['admin'] // or you can only set roles in sub nav
  //       }
  //     },
  //     {
  //       path: 'directive',
  //       component: () => import(/* webpackChunkName: "permission-directive" */ '@/views/permission/directive.vue'),
  //       name: 'DirectivePermission',
  //       meta: {
  //         title: 'directivePermission'
  //         // if do not set roles, means: this page does not require permission
  //       }
  //     },
  //     {
  //       path: 'role',
  //       component: () => import(/* webpackChunkName: "permission-role" */ '@/views/permission/role.vue'),
  //       name: 'RolePermission',
  //       meta: {
  //         title: 'rolePermission',
  //         roles: ['admin']
  //       }
  //     }
  //   ]
  // },
  /** when your routing map is too long, you can split it into small modules **/
  subscriptionRouter,
  // componentsRouter,
  // chartsRouter,
  // nestedRouter,
  // tableRouter,
  // {
  //   path: '/example',
  //   component: Layout,
  //   redirect: '/example/list',
  //   meta: {
  //     title: 'example',
  //     icon: 'example',
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'create',
  //       component: () => import(/* webpackChunkName: "example-create" */ '@/views/example/create.vue'),
  //       name: 'CreateArticle',
  //       meta: {
  //         title: 'createArticle',
  //         icon: 'edit'
  //       }
  //     },
  //     {
  //       path: 'edit/:id(\\d+)',
  //       component: () => import(/* webpackChunkName: "example-edit" */ '@/views/example/edit.vue'),
  //       name: 'EditArticle',
  //       meta: {
  //         title: 'editArticle',
  //         noCache: true,
  //         activeMenu: '/example/list',
  //         hidden: true
  //       }
  //     },
  //     {
  //       path: 'list',
  //       component: () => import(/* webpackChunkName: "example-list" */ '@/views/example/list.vue'),
  //       name: 'ArticleList',
  //       meta: {
  //         title: 'articleList',
  //         icon: 'list'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/tab',
  //   component: Layout,
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "tab" */ '@/views/tab/index.vue'),
  //       name: 'Tab',
  //       meta: {
  //         title: 'tab',
  //         icon: 'tab'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/error',
  //   component: Layout,
  //   redirect: 'noredirect',
  //   meta: {
  //     title: 'errorPages',
  //     icon: '404',
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: '401',
  //       component: () => import(/* webpackChunkName: "error-page-401" */ '@/views/error-page/401.vue'),
  //       name: 'Page401',
  //       meta: {
  //         title: 'page401',
  //         noCache: true
  //       }
  //     },
  //     {
  //       path: '404',
  //       component: () => import(/* webpackChunkName: "error-page-404" */ '@/views/error-page/404.vue'),
  //       name: 'Page404',
  //       meta: {
  //         title: 'page404',
  //         noCache: true
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/error-log',
  //   component: Layout,
  //   redirect: 'noredirect',
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'log',
  //       component: () => import(/* webpackChunkName: "error-log" */ '@/views/error-log/index.vue'),
  //       name: 'ErrorLog',
  //       meta: {
  //         title: 'errorLog',
  //         icon: 'bug'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/excel',
  //   component: Layout,
  //   redirect: '/excel/export-excel',
  //   meta: {
  //     title: 'excel',
  //     icon: 'excel',
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'export-excel',
  //       component: () => import(/* webpackChunkName: "export-excel" */ '@/views/excel/export-excel.vue'),
  //       name: 'ExportExcel',
  //       meta: { title: 'exportExcel' }
  //     },
  //     {
  //       path: 'export-selected-excel',
  //       component: () => import(/* webpackChunkName: "select-excel" */ '@/views/excel/select-excel.vue'),
  //       name: 'SelectExcel',
  //       meta: { title: 'selectExcel' }
  //     },
  //     {
  //       path: 'export-merge-header',
  //       component: () => import(/* webpackChunkName: "merge-header" */ '@/views/excel/merge-header.vue'),
  //       name: 'MergeHeader',
  //       meta: { title: 'mergeHeader' }
  //     },
  //     {
  //       path: 'upload-excel',
  //       component: () => import(/* webpackChunkName: "upload-excel" */ '@/views/excel/upload-excel.vue'),
  //       name: 'UploadExcel',
  //       meta: { title: 'uploadExcel' }
  //     }
  //   ]
  // },
  // {
  //   path: '/zip',
  //   component: Layout,
  //   redirect: '/zip/download',
  //   meta: {
  //     title: 'zip',
  //     icon: 'zip',
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'download',
  //       component: () => import(/* webpackChunkName: "zip" */ '@/views/zip/index.vue'),
  //       name: 'ExportZip',
  //       meta: {
  //         title: 'exportZip',
  //         roles: ['admin']
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/pdf',
  //   component: Layout,
  //   redirect: '/pdf/index',
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "pdf" */ '@/views/pdf/index.vue'),
  //       name: 'PDF',
  //       meta: {
  //         title: 'pdf',
  //         icon: 'pdf',
  //         roles: ['admin']
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/pdf-download-example',
  //   component: () => import(/* webpackChunkName: "pdf-download-example" */ '@/views/pdf/download.vue'),
  //   meta: {
  //     hidden: true,
  //     roles: ['admin']
  //   }
  // },
  // {
  //   path: '/theme',
  //   component: Layout,
  //   redirect: 'noredirect',
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "theme" */ '@/views/theme/index.vue'),
  //       name: 'Theme',
  //       meta: {
  //         title: 'theme',
  //         icon: 'theme'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/clipboard',
  //   component: Layout,
  //   redirect: 'noredirect',
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "clipboard" */ '@/views/clipboard/index.vue'),
  //       name: 'Clipboard',
  //       meta: {
  //         title: 'clipboard',
  //         icon: 'clipboard'
  //       }
  //     }
  //   ]
  // },
  // {
  //   path: '/i18n',
  //   component: Layout,
  //   meta: {
  //     roles: ['admin']
  //   },
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "i18n-demo" */ '@/views/i18n-demo/index.vue'),
  //       name: 'I18n',
  //       meta: {
  //         title: 'i18n',
  //         icon: 'international'
  //       }
  //     }
  //   ]
  // },
  {
    path: '*',
    redirect: '/404',
    meta: { hidden: true }
  }
]

const createRouter = () =>
  new VueRouter({
    // mode: 'history',  // Disabled due to Github Pages doesn't support this, enable this if you need.
    scrollBehavior: (to, from, savedPosition) => {
      if (savedPosition) {
        return savedPosition
      } else {
        return { x: 0, y: 0 }
      }
    },
    base: process.env.BASE_URL,
    routes: constantRoutes
  })

const router = createRouter()

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
  const newRouter = createRouter();
  (router as any).matcher = (newRouter as any).matcher // reset router
}

export default router
